import type { MenuEntry } from "./binding";
import type { BuxusOperatorPayload } from "../payload/types";
import {
  checkHasHazardNavAsset,
  checkHasInAnEmergencyAsset,
  hasAssetDirectory,
  hasAssetDocumentsMenuEntryVisible,
  hasGeneralHazards,
  hasOperatorMarkerImagesForAsset,
  hasSpecialNotes,
  listAssetEmergencySidebarSubpages,
  listAssetEmergencySubpages,
  assetProfileNavLabel,
} from "./assetScreenHelpers";
import { assetIdAndName, getVisibleAssets, type AssetVisibilityContext } from "./legacyOperatorGates";

export type AssetMenuContext = AssetVisibilityContext;

export type AssetMenuMergeOptions = {
  /**
   * When set (web): inject at most one asset branch under Assets — the sidebar context asset.
   * Persists on the assets hub so the tree stays stable while content has no active asset.
   * When omitted (mobile): inject every visible asset branch (legacy behavior).
   */
  selectedAssetId?: string | null;
  /** Web: on assets hub or any asset screen (controls hub-only branch injection when no selected asset). */
  inAssetsSection?: boolean;
};

/** Matches legacy nav: operator → assets hub → asset overview, then per-asset leaves. */
export const ASSET_BASE_NAV_STACK = [
  "screen.operatorOverview",
  "screen.assets",
  "screen.assetOverview",
] as const;

export const ASSET_PROFILE_NAV_STACK = [...ASSET_BASE_NAV_STACK, "screen.assetProfile"] as const;

function assetSubmenuSkeleton(
  payload: BuxusOperatorPayload,
  ctx: AssetMenuContext,
  asset: Record<string, unknown>,
  assetId: string,
  prefix: string,
  webSidebarDedupeErDuplicates: boolean,
): MenuEntry[] {
  const base = [...ASSET_BASE_NAV_STACK];
  const op = payload.operator as Record<string, unknown>;
  const stackAcc = base;
  const withNav = (node: MenuEntry): MenuEntry => ({
    ...node,
    id: `${prefix}:${node.id}`,
    assetId,
    navStack: node.screenId ? [...stackAcc, node.screenId] : stackAcc,
  });

  const staticNodes: MenuEntry[] = [
    withNav({
      id: "sk.profile",
      label: assetProfileNavLabel(asset),
      screenId: "screen.assetProfile",
    }),
  ];
  if (checkHasInAnEmergencyAsset(asset)) {
    const erSubpages = webSidebarDedupeErDuplicates
      ? listAssetEmergencySidebarSubpages(asset, assetId, ctx)
      : listAssetEmergencySubpages(asset, assetId, ctx);
    if (erSubpages.length > 0) {
      /** Hub + children: screenId enables sidebar navigation back to the ER hub. */
      staticNodes.push({
        id: `${prefix}:sk.emergencyResponse`,
        label: "Emergency Response",
        screenId: "screen.assetEmergencyResponse",
        assetId,
        navStack: [...stackAcc, "screen.assetEmergencyResponse"],
        children: erSubpages.map((sp) => ({
          id: `${prefix}:${sp.menuId}`,
          label: sp.label,
          screenId: sp.screenId,
          assetId,
          navStack: [...stackAcc, "screen.assetEmergencyResponse", sp.screenId],
        })),
      });
    } else {
      staticNodes.push(
        withNav({
          id: "sk.emergencyResponse",
          label: "Emergency Response",
          screenId: "screen.assetEmergencyResponse",
        }),
      );
    }
  }
  if (hasOperatorMarkerImagesForAsset(op, asset)) {
    staticNodes.push(withNav({ id: "sk.lineMarkers", label: "Line Markers", screenId: "screen.assetLineMarkers" }));
  }
  if (checkHasHazardNavAsset(asset)) {
    staticNodes.push(
      withNav({
        id: "sk.products",
        label: "Products & Hazards",
        screenId: "screen.assetProductsHazards",
      }),
    );
  }
  if (hasAssetDirectory(asset)) {
    staticNodes.push(withNav({ id: "sk.keyLocations", label: "Key Locations", screenId: "screen.assetKeyLocations" }));
  }
  if (hasAssetDocumentsMenuEntryVisible(asset, assetId, ctx)) {
    staticNodes.push(
      withNav({ id: "sk.documents", label: "Documents & Plans", screenId: "screen.assetDocuments" }),
    );
  }
  if (hasGeneralHazards(asset)) {
    staticNodes.push(
      withNav({ id: "sk.generalHazards", label: "General Hazards", screenId: "screen.assetGeneralHazards" }),
    );
  }
  if (hasSpecialNotes(asset)) {
    staticNodes.push(withNav({ id: "sk.specialNotes", label: "Special Notes", screenId: "screen.assetSpecialNotes" }));
  }
  return staticNodes;
}

function buildAssetMenuBranch(
  payload: BuxusOperatorPayload,
  ctx: AssetMenuContext,
  assetId: string,
  label: string,
  asset: Record<string, unknown>,
  webSidebarDedupeErDuplicates: boolean,
): MenuEntry {
  const prefix = `nav.asset.${assetId}`;
  const baseStack = [...ASSET_BASE_NAV_STACK];
  return {
    id: prefix,
    label,
    screenId: "screen.assetOverview",
    linkLabel: "Asset Overview",
    assetId,
    navStack: [...baseStack],
    defaultOpen: true,
    children: assetSubmenuSkeleton(payload, ctx, asset, assetId, prefix, webSidebarDedupeErDuplicates),
  };
}

/**
 * Injects asset submenu branches under `nav.assets`.
 * Web passes `selectedAssetId` so only the active asset appears; mobile omits it for the full list.
 */
export function mergeAssetBranchesIntoMenu(
  tree: MenuEntry[],
  payload: BuxusOperatorPayload,
  ctx: AssetMenuContext,
  options?: AssetMenuMergeOptions,
): MenuEntry[] {
  const webMode = options !== undefined;
  const sidebarAssetId = options?.selectedAssetId ?? null;
  const inAssetsSection = webMode && (options?.inAssetsSection ?? false);

  return tree.map((node) => {
    if (node.id === "nav.assets") {
      const baseChildren = node.children ?? [];
      const assets = getVisibleAssets(payload, ctx);
      const assetNodes: MenuEntry[] = [];
      const hubNavStack = ["screen.operatorOverview", "screen.assets"];
      const multiAsset = assets.length > 1;

      if (webMode) {
        /**
         * Keep the contextual asset branch in the sidebar whenever the user has a sidebar asset
         * context (including after navigating to Safety, etc.) so the tree does not reshuffle.
         */
        if (sidebarAssetId) {
          const row = assets.find((a) => {
            const pair = assetIdAndName(a);
            return pair?.id === sidebarAssetId;
          });
          const pair = row ? assetIdAndName(row) : null;
          if (pair) {
            assetNodes.push(
              buildAssetMenuBranch(
                payload,
                ctx,
                pair.id,
                pair.label,
                row as Record<string, unknown>,
                true,
              ),
            );
            if (multiAsset) {
              assetNodes.push({
                id: "nav.assets.other",
                label: "All Assets",
                screenId: "screen.assets",
                navStack: hubNavStack,
              });
            }
          }
        } else if (inAssetsSection && assets.length === 1) {
          const pair = assetIdAndName(assets[0]!);
          if (pair) {
            assetNodes.push(
              buildAssetMenuBranch(
                payload,
                ctx,
                pair.id,
                pair.label,
                assets[0] as Record<string, unknown>,
                true,
              ),
            );
          }
        }
        /** Multi-asset hub with no sidebar asset: content list only — no flat sidebar duplicate. */
      } else if (!webMode) {
        for (const row of assets) {
          const pair = assetIdAndName(row);
          if (pair) {
            assetNodes.push(
              buildAssetMenuBranch(
                payload,
                ctx,
                pair.id,
                pair.label,
                row as Record<string, unknown>,
                false,
              ),
            );
          }
        }
      }

      return {
        ...node,
        label: webMode && multiAsset ? `Assets (${assets.length})` : node.label,
        /** Open state lives in shell `navOpenById` — do not flip defaultOpen by route (disorienting). */
        children: [...baseChildren, ...assetNodes],
      };
    }
    if (node.children?.length) {
      return { ...node, children: mergeAssetBranchesIntoMenu(node.children, payload, ctx, options) };
    }
    return node;
  });
}
